"use client";

import { createClient } from "@/lib/supabase/client";
import type { User } from "@supabase/supabase-js";
import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { fcmClientService } from "@/lib/fcm/client";
import {
  bootstrapBrowserSessionFromServerCookies,
  syncSupabaseSessionToServer,
} from "@/lib/supabase/sync-server-session";
import { queryClient, queryKeys } from "@/lib/query-client";
import { safeSignOutLocalClient } from "@/lib/auth/safe-sign-out-client";

/** TanStack Query requires queryFn to never return `undefined`. */
export type AuthBootstrapResult =
  | { status: "already_local" }
  | { status: "no_server_session" }
  | { status: "hydrated" };

async function ensureSessionFromCookiesOrLocal(): Promise<AuthBootstrapResult> {
  const supabase = createClient();
  const {
    data: { session: existing },
    error,
  } = await supabase.auth.getSession();
  if (error) throw error;
  if (existing) {
    await syncSupabaseSessionToServer(existing);
    return { status: "already_local" };
  }

  const boot = await bootstrapBrowserSessionFromServerCookies();
  if (!boot) return { status: "no_server_session" };

  const { error: setErr } = await supabase.auth.setSession({
    access_token: boot.access_token,
    refresh_token: boot.refresh_token,
  });
  if (setErr) throw setErr;
  return { status: "hydrated" };
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  /** True after first read of client session post-bootstrap (avoids loading=false while user is still null). */
  const [hydrated, setHydrated] = useState(false);
  const [mounted, setMounted] = useState(false);
  const supabase = useMemo(() => createClient(), []);
  const router = useRouter();

  useEffect(() => {
    setMounted(true);
  }, []);

  const bootstrapQuery = useQuery({
    queryKey: queryKeys.auth.bootstrapSession,
    queryFn: ensureSessionFromCookiesOrLocal,
    enabled: mounted,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const loading =
    !mounted ||
    bootstrapQuery.isPending ||
    (bootstrapQuery.isFetched && !bootstrapQuery.isError && !hydrated);

  useEffect(() => {
    if (!mounted || !bootstrapQuery.isFetched) return;

    if (bootstrapQuery.isError) {
      console.error("Auth bootstrap error:", bootstrapQuery.error);
      setUser(null);
      setHydrated(true);
      return;
    }

    void supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setUser(session?.user ?? null);
        setHydrated(true);
      })
      .catch(() => {
        setUser(null);
        setHydrated(true);
      });
  }, [mounted, bootstrapQuery.isFetched, bootstrapQuery.isError, bootstrapQuery.error, supabase]);

  useEffect(() => {
    if (!mounted) return;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      // Keep UI from treating "no user yet" as logged out while session is mid-resolve on a new hook instance.
      setHydrated(true);
    });

    return () => subscription.unsubscribe();
  }, [supabase, mounted]);

  const signOut = async () => {
    if (typeof window !== "undefined") sessionStorage.setItem("explicit_logout", "1");

    try {
      try {
        await fcmClientService.unregisterToken();
      } catch (fcmError) {
        console.warn("FCM token unregister failed during logout:", fcmError);
      }

      // Client GoTrue sign-out FIRST while local refresh token is still valid.
      // clear-session revokes server/cookie session and must run after, or POST /logout returns 403.
      await safeSignOutLocalClient(supabase);

      try {
        await fetch("/api/auth/clear-session", { method: "POST", credentials: "same-origin" });
      } catch {
        /* non-blocking */
      }

      queryClient.removeQueries({ queryKey: queryKeys.auth.bootstrapSession });

      toast.success("Logged out successfully");
      router.push("/");
    } catch (error) {
      console.error("Error in signOut:", error);
      toast.success("Logged out successfully");
      router.push("/");
    } finally {
      if (typeof window !== "undefined") sessionStorage.removeItem("explicit_logout");
    }
  };

  if (!mounted) {
    return {
      user: null,
      loading: true,
      signOut,
      isAuthenticated: false,
    };
  }

  return {
    user,
    loading,
    signOut,
    isAuthenticated: !!user,
  };
}
